// src/components/dashboard/DashboardHistoryTab.jsx
// ============================================================
// PREVIOUS BEERS TAB - Shows beer history across all kegs
// ============================================================
// Aggregates beer history from all kegs with ratings display.
// Props: kegs, account, calculateAverageRating, getTotalRatings, 
//        renderStars, onViewHistory
// ============================================================

import React from 'react';

// Helper to pick the most recent session (prefer ended_date, fallback to kicked_date)
const getLatestSession = (sessions) => {
  if (!sessions || sessions.length === 0) return null;
  return sessions
    .slice()
    .sort((a, b) => {
      const aDate = new Date(a.ended_date || a.kicked_date);
      const bDate = new Date(b.ended_date || b.kicked_date);
      return bDate - aDate;
    })[0];
};

const formatDuration = (start, end = new Date()) => {
  if (!start) return null;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const diffMs = e - s;
  if (diffMs <= 0) return null;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours >= 1) return `${hours}h`;
  const minutes = Math.floor(diffMs / (1000 * 60));
  return `${minutes}m`;
};

/**
 * HistoryBeerCard - Card for a single beer in history
 */
const HistoryBeerCard = ({ 
  beer, 
  account, 
  calculateAverageRating, 
  getTotalRatings, 
  renderStars,
  onViewHistory 
}) => (
  <div 
    className="border-l-4 border-amber-500 pl-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition flex gap-4" 
    onClick={() => onViewHistory(beer)}
  >
    {/* Beer Image */}
    {(beer.imageUrl || account?.profile?.logoUrl) && (
      <img 
        src={beer.imageUrl || account.profile.logoUrl} 
        alt={beer.name} 
        className="w-20 h-20 object-cover rounded flex-shrink-0" 
      />
    )}
    
    {/* Beer Details */}
    <div className="flex-1">
      <h5 className="text-lg font-semibold text-amber-600 hover:text-amber-700">
        {beer.name}
      </h5>
      <p className="text-sm text-gray-700 dark:text-gray-300">
        {beer.style} • {beer.abv}% ABV {beer.ibu && `• ${beer.ibu} IBU`}
      </p>
      
      {/* Rating Stars */}
      <div className="flex items-center gap-2 mt-1">
        {renderStars(calculateAverageRating(beer.sessions))}
        <span className="text-xs text-gray-600 dark:text-gray-400">
          ({getTotalRatings(beer.sessions)} rating{getTotalRatings(beer.sessions) !== 1 ? 's' : ''})
        </span>
      </div>
      
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
        Last served on keg: {beer.kegName}
      </p>

      {/* Latest session summary: volume and duration */}
      {(() => {
        const latest = getLatestSession(beer.sessions);
        if (!latest) return null;
        const duration = formatDuration(latest.kicked_date, latest.ended_date);
        return (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            {latest.volume ? `${latest.volume} gal` : ''}{latest.volume && duration ? ' • ' : ''}{duration ? duration : ''}{!duration && !latest.volume ? 'Session info not available' : ''}
          </p>
        );
      })()}

    </div>
  </div>
);

/**
 * DashboardHistoryTab - Main history tab component
 * Flattens beer history from all kegs into a single list
 */
export const DashboardHistoryTab = ({ 
  kegs, 
  account,
  calculateAverageRating,
  getTotalRatings,
  renderStars,
  onViewHistory 
}) => {
  // Flatten all beer history from all kegs
  const allBeerHistory = kegs.flatMap(keg => 
    keg.beerHistory.map(beer => ({ 
      ...beer, 
      kegName: keg.name,
      kegId: keg.id 
    }))
  );

  if (allBeerHistory.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-gray-500 dark:text-gray-400 text-center">
          No previous beers yet. Kick your first keg to see history here!
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="space-y-4">
        {allBeerHistory.map((beer, idx) => (
          <HistoryBeerCard
            key={`${beer.kegId}-${beer.name}-${idx}`}
            beer={beer}
            account={account}
            calculateAverageRating={calculateAverageRating}
            getTotalRatings={getTotalRatings}
            renderStars={renderStars}
            onViewHistory={onViewHistory}
          />
        ))}
      </div>
    </div>
  );
};

export default DashboardHistoryTab;
